/* eslint-disable no-param-reassign */
import { createSlice } from '@reduxjs/toolkit';
import { convertirANumero } from '../../utils/currency';
import { storePerfiladorValuePropiety } from '../../utils';
import {
  DEFAULT_GASTOS_ESCRITURA_PERCENTAGE,
  DEFAULT_PLAZO_PERFILADOR,
  DEFAULT_TASAS,
  INPUT_SIMULATOR_MINIMUN_ENGANCHE_PERCENTAGE,
} from '../../constants';
import { calcAmmountEngancheFromMontoCredito } from '../../utils/simulator.utils';

// Redondea a 2 decimales conservando el número
const round2 = (n) => Number(n.toFixed(2));
// Redondea a 2 decimales y devuelve el string con exactamente 2 decimales (sin ceros perdidos)
const fix2 = (n) => n.toFixed(2);

// Helper para obtener el monto total de Infonavit (subcuenta + crédito)
const obtenerMontoTotalInfonavit = (state) => {
  const { saldoSubcuentaInfonavit = 0, montoCreditoInfonavit = 0 } = state;
  return saldoSubcuentaInfonavit + montoCreditoInfonavit;
};

// Helper para recalcular enganche recursos propios basado en montos de Infonavit
const recalcularEngancheConSubcuenta = (state) => {
  const { inputNumber, engancheMinimoPercentage } = state;

  // Obtener el monto total de Infonavit (subcuenta + crédito)
  const montoTotalInfonavit = obtenerMontoTotalInfonavit(state);

  const engancheMinimoRequerido = round2((inputNumber * engancheMinimoPercentage) / 100);

  // Si montoTotalInfonavit cubre o supera el enganche mínimo, recursos propios = 0
  if (montoTotalInfonavit >= engancheMinimoRequerido) {
    state.engancheRecursosPropiosValue = '0.00';
    state.engancheRecursosPropiosNumber = 0;
  } else {
    const recursosRestantes = round2(engancheMinimoRequerido - montoTotalInfonavit);
    state.engancheRecursosPropiosValue = fix2(recursosRestantes);
    state.engancheRecursosPropiosNumber = recursosRestantes;
  }

  const engancheTotal = round2(montoTotalInfonavit + state.engancheRecursosPropiosNumber);
  state.engancheNumber = engancheTotal;
  state.engancheValue = fix2(engancheTotal);
};

// Helper para actualizar enganche total cuando cambian los recursos propios
const actualizarEngancheTotal = (state) => {
  const { engancheRecursosPropiosNumber } = state;
  const montoTotalInfonavit = obtenerMontoTotalInfonavit(state);
  const engancheTotal = round2(montoTotalInfonavit + engancheRecursosPropiosNumber);
  state.engancheNumber = engancheTotal;
  state.engancheValue = fix2(engancheTotal);
};

const initialState = {
  inputValue: '',
  inputNumber: 0,
  resultado: {},
  montoCreditoValue: 0,
  montoCreditoNumber: 0,
  monthlyPay: 0,
  initialDataValues: {},
  engancheValue: 0, // Enganche total (montoTotalInfonavit + recursos propios)
  engancheNumber: 0,
  engancheRecursosPropiosValue: 0, // Recursos propios que el usuario aporta
  engancheRecursosPropiosNumber: 0,
  tasaNumber: DEFAULT_TASAS[convertirANumero(DEFAULT_PLAZO_PERFILADOR)],
  plazo: DEFAULT_PLAZO_PERFILADOR,
  plazoNumber: convertirANumero(DEFAULT_PLAZO_PERFILADOR),
  engancheMinimoPercentage: INPUT_SIMULATOR_MINIMUN_ENGANCHE_PERCENTAGE,
  engancheMinimoPercentageRecursosPropios: 0,
  gastosEscrituraPercentage: DEFAULT_GASTOS_ESCRITURA_PERCENTAGE,
  percentageEnganche: 0, // Porcentaje del enganche total
  percentageEngancheRecursosPropios: 0, // Porcentaje de recursos propios
  generalConditions: {
    engancheMinimoPercentage: INPUT_SIMULATOR_MINIMUN_ENGANCHE_PERCENTAGE,
    gastosEscrituraPercentage: DEFAULT_GASTOS_ESCRITURA_PERCENTAGE,
    plazo: DEFAULT_PLAZO_PERFILADOR,
    plazoNumber: convertirANumero(DEFAULT_PLAZO_PERFILADOR),
  },
  inmobiliariasConditions: {}, // Objeto para almacenar condiciones por inmobiliariaId
  currentInmobiliariaId: null, // Para manejar la inmobiliaria seleccionada
  isMontoCreditoManual: false, // Flag para indicar si montoCredito fue modificado manualmente
  montoCreditoInfonavit: 0, // Número (no formateado)
  saldoSubcuentaInfonavit: 0, // Número (no formateado)
  // Rangos de enganche de recursos propios (ya considerando descuento de Infonavit)
  engancheRangos: {
    minimo: 0,
    maximo: 0,
  },
  // Rangos de monto de crédito (sincronizado desde selector calculado)
  montoCreditoRangos: {
    minimo: 0,
    maximo: 0,
  },
};

export const perfiladorSlice = createSlice({
  name: 'perfilador',
  initialState,
  reducers: {
    setPerfiladorInputValue: (state, action) => {
      state.inputValue = action.payload;
      state.inputNumber = convertirANumero(action.payload);
      state.isMontoCreditoManual = false; // Resetear flag cuando cambia precio directamente
    },
    setPerfiladorInputValueAndCalcEnganche: (state, action) => {
      const { value } = action.payload;
      const valueNumber = convertirANumero(value);
      state.inputValue = value;
      state.inputNumber = valueNumber;
      state.isMontoCreditoManual = false; // Resetear flag cuando cambia precio

      // Recalcular enganche considerando montoSubcuenta
      recalcularEngancheConSubcuenta(state);
    },
    setPerfiladorResultado: (state, action) => {
      state.resultado = action.payload;
    },
    setInitialPerfiladorPrecioInmueble: (state, action) => {
      const { precio, initialEnganche = '', initialPlazo = '' } = action.payload;
      const valueNumber = convertirANumero(precio);

      state.inputValue = precio;
      state.inputNumber = valueNumber;
      state.isMontoCreditoManual = false; // Resetear flag cuando se inicializa precio

      if (initialEnganche) {
        // Si se provee un enganche inicial, asumimos que es el enganche total
        const engancheTotal = round2(convertirANumero(initialEnganche));
        const montoTotalInfonavit = obtenerMontoTotalInfonavit(state);
        const recursosPropios = round2(Math.max(0, engancheTotal - montoTotalInfonavit));

        state.engancheRecursosPropiosValue = fix2(recursosPropios);
        state.engancheRecursosPropiosNumber = recursosPropios;
        state.engancheValue = initialEnganche;
        state.engancheNumber = engancheTotal;
      } else {
        // Recalcular enganche considerando montos de Infonavit
        recalcularEngancheConSubcuenta(state);
      }

      if (initialPlazo) {
        state.plazo = initialPlazo;
        state.plazoNumber = convertirANumero(initialPlazo);
      }

      storePerfiladorValuePropiety(precio);
    },
    setPerfiladorMontoCredito: (state, action) => {
      const newMontoCreditoNumber = convertirANumero(action.payload);
      const {
        engancheNumber: engancheTotalCalculado,
        enganche: engancheTotalFormatted,
      } = calcAmmountEngancheFromMontoCredito(
        newMontoCreditoNumber,
        state.inputNumber,
      );

      // El enganche calculado es el total, debemos separar recursos propios
      const montoTotalInfonavit = obtenerMontoTotalInfonavit(state);
      const recursosPropios = round2(Math.max(0, engancheTotalCalculado - montoTotalInfonavit));

      state.resultado.montoCredito = newMontoCreditoNumber;
      state.engancheNumber = engancheTotalCalculado;
      state.engancheValue = engancheTotalFormatted;
      state.engancheRecursosPropiosNumber = recursosPropios;
      state.engancheRecursosPropiosValue = fix2(recursosPropios);
      state.isMontoCreditoManual = true; // Activar flag cuando se modifica manualmente
    },
    setPerfiladorMonthlyPay: (state, action) => {
      state.monthlyPay = action.payload;
    },
    setPerfiladorPlazo: (state, action) => {
      const plazo = action.payload;
      const plazoNumber = convertirANumero(plazo);
      const tasaNumber = DEFAULT_TASAS[plazoNumber];

      state.plazo = plazo;
      state.plazoNumber = plazoNumber;
      state.tasaNumber = tasaNumber;
    },
    setPerfiladorInitialDataValues: (state, action) => {
      state.initialDataValues = action.payload;
    },
    setPerfiladorEnganche: (state, action) => {
      // Ahora modifica los recursos propios del usuario
      state.engancheRecursosPropiosValue = action.payload;
      state.engancheRecursosPropiosNumber = convertirANumero(action.payload);
      state.isMontoCreditoManual = false; // Resetear flag cuando cambia enganche

      // Actualizar enganche total (enganche = montoTotalInfonavit + recursos propios)
      actualizarEngancheTotal(state);
    },
    setPerfiladorPercentageEnganche: (state, action) => {
      state.percentageEnganche = action.payload;
    },
    setPerfiladorPercentageEngancheRecursosPropios: (state, action) => {
      state.percentageEngancheRecursosPropios = action.payload;
    },
    setPerfiladorEngancheRecursosPropios: (state, action) => {
      state.engancheRecursosPropiosValue = action.payload;
      state.engancheRecursosPropiosNumber = convertirANumero(action.payload);
      state.isMontoCreditoManual = false; // Resetear flag cuando cambia enganche

      // Actualizar enganche total (enganche = montoTotalInfonavit + recursos propios)
      actualizarEngancheTotal(state);
    },
    setPerfiladorInitialValues: (state, action) => {
      const {
        precio,
        enganche,
        plazo,
        engancheRecursosPropios,
        saldoSubcuentaInfonavit,
        montoCreditoInfonavit,
      } = action.payload;

      const currentConditions = state.currentInmobiliariaId
        ? { ...state.inmobiliariasConditions[state.currentInmobiliariaId] }
        : { ...state.generalConditions };

      // Establecer precio
      state.inputValue = precio || state.inputValue;
      state.inputNumber = convertirANumero(precio) || state.inputNumber;

      // Establecer montos de Infonavit si se proporcionan
      if (saldoSubcuentaInfonavit !== undefined) {
        state.saldoSubcuentaInfonavit = typeof saldoSubcuentaInfonavit === 'number'
          ? saldoSubcuentaInfonavit
          : convertirANumero(saldoSubcuentaInfonavit);
      }

      if (montoCreditoInfonavit !== undefined) {
        state.montoCreditoInfonavit = typeof montoCreditoInfonavit === 'number'
          ? montoCreditoInfonavit
          : convertirANumero(montoCreditoInfonavit);
      }

      // Calcular montoTotalInfonavit después de establecer los valores
      const montoTotalInfonavit = obtenerMontoTotalInfonavit(state);

      // Manejar el enganche
      if (engancheRecursosPropios !== undefined) {
        // Si se provee engancheRecursosPropios directamente, usarlo
        const recursosPropiosNumber = typeof engancheRecursosPropios === 'number'
          ? engancheRecursosPropios
          : convertirANumero(engancheRecursosPropios);

        state.engancheRecursosPropiosValue = fix2(recursosPropiosNumber);
        state.engancheRecursosPropiosNumber = recursosPropiosNumber;

        // Calcular enganche total
        const engancheTotal = round2(montoTotalInfonavit + recursosPropiosNumber);
        state.engancheNumber = engancheTotal;
        state.engancheValue = fix2(engancheTotal);
      } else if (enganche) {
        // Si se provee enganche total, calcular recursos propios
        const engancheTotal = round2(convertirANumero(enganche));
        const recursosPropios = round2(Math.max(0, engancheTotal - montoTotalInfonavit));

        state.engancheRecursosPropiosValue = fix2(recursosPropios);
        state.engancheRecursosPropiosNumber = recursosPropios;
        state.engancheValue = enganche;
        state.engancheNumber = engancheTotal;
      } else {
        // Si no se provee nada, recalcular todo
        recalcularEngancheConSubcuenta(state);
      }

      // Establecer plazo
      state.plazo = plazo || currentConditions.plazo;
      state.plazoNumber = convertirANumero(plazo) || currentConditions.plazoNumber;
      state.tasaNumber = DEFAULT_TASAS[convertirANumero(plazo)] || state.tasaNumber;

      // No modificamos generalConditions, solo usamos las condiciones según corresponda.
    },
    setInmobiliariaConditions: (state, action) => {
      const { inmobiliariaId, creditConditions } = action.payload;
      const { engancheMinimoPercentage, gastosEscrituraPercentage, plazo } = creditConditions;
      const objNewConditions = {
        engancheMinimoPercentage: convertirANumero(engancheMinimoPercentage),
        gastosEscrituraPercentage: convertirANumero(gastosEscrituraPercentage),
        plazo,
        plazoNumber: convertirANumero(plazo),
      };

      state.inmobiliariasConditions[inmobiliariaId] = {
        ...state.generalConditions,
        ...objNewConditions,
      };
    },
    setCurrentInmobiliariaId: (state, action) => {
      state.currentInmobiliariaId = action.payload;
    },
    clearInmobiliariaConditions: (state) => {
      state.inmobiliariasConditions = {};
      state.currentInmobiliariaId = null;
    },
    setPerfiladorGeneralConditions: (state) => {
      state.plazo = state.generalConditions.plazo;
      state.plazoNumber = state.generalConditions.plazoNumber;
      state.tasaNumber = DEFAULT_TASAS[state.generalConditions.plazoNumber];
      state.engancheMinimoPercentage = state.generalConditions.engancheMinimoPercentage;
      state.gastosEscrituraPercentage = state.generalConditions.gastosEscrituraPercentage;
    },
    setConditionsByInmobiliariaId: (state, action) => {
      const { inmobiliariaId } = action.payload;
      const inmobiliariaConditions = state.inmobiliariasConditions[inmobiliariaId]
        ? { ...state.inmobiliariasConditions[inmobiliariaId] }
        : { ...state.generalConditions };

      state.plazo = inmobiliariaConditions.plazo || state.generalConditions.plazo;
      state.plazoNumber = inmobiliariaConditions.plazoNumber || state.generalConditions.plazoNumber;
      state.tasaNumber = DEFAULT_TASAS[inmobiliariaConditions.plazoNumber] || state.tasaNumber;
      state.engancheMinimoPercentage = inmobiliariaConditions.engancheMinimoPercentage
        || state.generalConditions.engancheMinimoPercentage;
      state.gastosEscrituraPercentage = inmobiliariaConditions.gastosEscrituraPercentage
        || state.generalConditions.gastosEscrituraPercentage;
    },
    setPerfiladorMontoCreditoInfonavit: (state, action) => {
      // Acepta tanto número como string formateado
      state.montoCreditoInfonavit = typeof action.payload === 'number'
        ? action.payload
        : convertirANumero(action.payload);

      // Recalcular enganche basado en el nuevo monto de crédito Infonavit
      if (state.inputNumber > 0) {
        recalcularEngancheConSubcuenta(state);
      }
    },
    setPerfiladorMontoSubcuenta: (state, action) => {
      state.saldoSubcuentaInfonavit = typeof action.payload === 'number'
        ? action.payload
        : convertirANumero(action.payload);

      if (state.inputNumber > 0) {
        recalcularEngancheConSubcuenta(state);
      }
    },
    /**
     * Establece los rangos de enganche de recursos propios
     * @param {Object} action.payload - { minimo, maximo, montoTotalInfonavit? }
     * Si se pasan valores brutos (sin restar Infonavit), se puede pasar montoTotalInfonavit
     * para que se reste automáticamente
     */
    setPerfiladorEngancheRangos: (state, action) => {
      const { minimo = 0, maximo = 0, restarInfonavit = false } = action.payload;

      if (restarInfonavit) {
        // Restar el monto de Infonavit para obtener rangos de recursos propios
        const montoTotalInfonavit = obtenerMontoTotalInfonavit(state);
        state.engancheRangos = {
          minimo: Math.max(0, minimo - montoTotalInfonavit),
          maximo: Math.max(0, maximo - montoTotalInfonavit),
        };
      } else {
        // Usar los valores directamente (ya vienen calculados)
        state.engancheRangos = { minimo, maximo };
      }
    },
    // Sincroniza rangos de monto de crédito (desde selector calculado)
    syncMontoCreditoRangos: (state, action) => {
      state.montoCreditoRangos = action.payload;
    },
  },
});

// envolver el componente dentro del mismo context

export const {
  setPerfiladorInputValue,
  setPerfiladorResultado,
  setInitialPerfiladorPrecioInmueble,
  setPerfiladorInputValueAndCalcEnganche,
  setPerfiladorMontoCredito,
  setPerfiladorMonthlyPay,
  setPerfiladorPlazo,
  setPerfiladorInitialDataValues,
  setPerfiladorEnganche,
  setPerfiladorPercentageEnganche,
  setPerfiladorPercentageEngancheRecursosPropios,
  setPerfiladorInitialValues,
  setPerfiladorGeneralConditions,
  setInmobiliariaConditions,
  setCurrentInmobiliariaId,
  clearInmobiliariaConditions,
  setConditionsByInmobiliariaId,
  setPerfiladorMontoCreditoInfonavit,
  setPerfiladorMontoSubcuenta,
  setPerfiladorEngancheRecursosPropios,
  setPerfiladorEngancheRangos,
  syncMontoCreditoRangos,
} = perfiladorSlice.actions;

export default perfiladorSlice.reducer;
